import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const PricingCard = ({
  id,
  title,
  price,
  description,
  features,
  isPrimary,
  ctaText,
  ctaLink,
  billingLabel
}: {
  key: string;
  title: string;
  price: string | null;
  description: string;
  features: string[];
  isPrimary?: boolean;
  ctaText: string;
  ctaLink: string;
  billingLabel?: string;
}) => {
  return <div className={`rounded-2xl p-6 sm:p-8 flex flex-col h-full ${isPrimary ? 'bg-gray-900 text-white shadow-xl md:scale-105 z-10' : 'bg-white border border-gray-200 text-gray-900'}`} data-id="element-448">
      <div className="mb-6 sm:mb-8" data-id="element-449">
        <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isPrimary ? 'text-white' : 'text-gray-900'}`} data-id="element-450">
          {title}
        </h3>
        <div className="flex items-baseline gap-1 mb-1" data-id="element-451">
          {price ? <>
              <motion.span key={price} className="text-3xl sm:text-4xl font-bold" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.25
          }} data-id="element-452">
                {price}
              </motion.span>
              <span className={`text-sm ${isPrimary ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-453">
                /month
              </span>
            </> : <span className="text-3xl sm:text-4xl font-bold" data-id="element-454">Custom</span>}
        </div>
        {billingLabel && <motion.p key={billingLabel} className={`text-xs mb-2 sm:mb-3 max-w-[200px] ${isPrimary ? 'text-gray-400' : 'text-gray-400'}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.2
      }} data-id="element-455">
            {billingLabel}
          </motion.p>}
        <p className={`text-sm ${isPrimary ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-456">
          {description}
        </p>
      </div>

      <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow" data-id="element-457">
        {features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-458">
            <CheckIcon size={18} className={`flex-shrink-0 mt-0.5 ${isPrimary ? 'text-orange-400' : 'text-orange-500'}`} data-id="element-459" />
            <span className={`text-sm ${isPrimary ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-460">
              {feature}
            </span>
          </li>)}
      </ul>

      <a href={ctaLink} target="_blank" onClick={() => window.gtag('event', 'signup_click', {
      source: `pricing_${id}`
    })} rel="noopener noreferrer" className={`w-full py-3 rounded-lg font-semibold text-center transition-colors block min-h-[44px] flex items-center justify-center ${isPrimary ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-id="element-461">
        {ctaText}
      </a>
    </div>;
};
const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const plans = [{
    id: 'free',
    title: 'Free Plan',
    price: 'Free',
    description: 'For exploration and early adoption',
    features: ['1 user', '3 forms', '25 submissions/month', 'AI form builder', 'Limited access to AI features'],
    ctaText: 'Start Free',
    ctaLink: 'https://accounts.fluid-form.ai/sign-up'
  }, {
    id: 'pro',
    title: 'Pro Plan',
    price: isAnnual ? '$33' : '$39',
    description: 'For teams and production workflows',
    features: ['3 users', '10 forms', '1,000 submissions/month', 'Advanced AI validations', 'Remove FluidForm branding'],
    isPrimary: true,
    ctaText: 'Go Pro',
    ctaLink: 'https://app.fluid-form.ai/billing',
    billingLabel: isAnnual ? 'Billed annually ($396/year)' : 'Billed monthly'
  }, {
    id: 'enterprise',
    title: 'Enterprise',
    price: null,
    description: 'For complex workflows and high-touch use cases',
    features: ['Custom pricing', 'Advanced voice & agent capabilities', 'Custom integrations', 'Priority support & SLA'],
    ctaText: 'Contact Sales',
    ctaLink: 'https://cal.com/fluidn/30min'
  }];
  return <section className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-[5.75rem]" id="pricing" data-id="element-462">
      <div className="container mx-auto px-4" data-id="element-463">
        <div className="text-center mb-10 sm:mb-12" data-id="element-464">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-465">
            Pricing
          </h2>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-full p-1" data-id="element-466">
            <button onClick={() => setIsAnnual(false)} className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all min-h-[44px] ${!isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-467">
              Monthly
            </button>
            <button onClick={() => setIsAnnual(true)} className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 sm:gap-2 min-h-[44px] ${isAnnual ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-468">
              Annual
              <span className="bg-orange-100 text-orange-600 text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-469">
                Save 15%
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8 items-stretch max-w-6xl mx-auto" data-id="element-470">
          {plans.map((plan, index) => <PricingCard key={index} {...plan} data-id="element-471" />)}
        </div>
      </div>
    </section>;
};
export default Pricing;